import React from 'react'
import * as d3 from 'd3-geo'

/* Rounds given number to given decimal point. */
function decimalRound(num, dec=2) {
    const div = Math.pow(10, dec)
    return Math.round((num + Number.EPSILON) * div) / div
}

/* Split given data into binCount arrays based on difference between each consecutive element in data. Make sure that data is sorted. */
function createBins(data, binCount) {
    /* Exit condition */
    if (binCount <= 1) {
        return [data]
    }
    /* Find mean value for data. */
    const mean = data.reduce((item1, item2) => (item1+item2), 0) / data.length
    const lowerCount = Math.max(Math.floor(binCount / 2), 1)
    return [
        ...createBins(data.filter(item => (item < mean)), lowerCount),
        ...createBins(data.filter(item => (item >= mean)), binCount-lowerCount)
    ]
}

/* Generate domain by creating bins from colorData. Domain is generated wrt Math.log10, where resultant values are rounded by exp-1. */
function generateDomain(colorData, colorList) {
    /* Define rounding function. */
    const expRound = (value) => {
        /* Determine rounding value. */
        const roundVal = Math.pow(10, Math.floor(Math.max(Math.log10(value) - 1, 0)))
        /* Return rounded value. */
        return Math.floor(value / roundVal) * roundVal
    }
    /* Format data. */
    const data = Array.from(
        Object.values(colorData).map((item) => (
            Math.max(decimalRound(Math.log10(item.confirmed), 2), 0)
        ))
    ).sort((item1, item2) => (item1-item2))
    /* Create bins. */
    const bins = createBins(data, colorList.length)
    /* Generate domain. */
    const domain = bins.map((entry) => (
        expRound(Math.pow(10, Math.max(...entry)))
    ))
    /* Return domain. */
    return domain
}

/* Empty function for unused events. */
const EMPTY_FUNC = ((args) => {})

/* Default colors for map. */
const DEFAULT_COLORS = [
    "#d8e4ff",
    "#aec2ef",
    "#6d8fdd",
    "#385cae",
    "#0f2e75",
]

/* Component for visualizing data on given geometry as SVG. */
export default class VectorMap extends React.Component {
    constructor(props) {
        super(props)
        const { colorList=DEFAULT_COLORS, colorData } = this.props;
        this.state = {
            colorList: colorList,
            domain: generateDomain(colorData, colorList)
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        /* Return update status. */
        return (
            /* Validate dimensions. */
            (this.props.width !== nextProps.width) || (this.props.height !== nextProps.height)
            /* Validate map data. */
            || (
                (this.props.mapData?.length !== nextProps.mapData?.length)
                || this.props.mapData?.some((item, index) => (nextProps.mapData[index] !== item)) 
            )
        )
    }

    render() {
        /* Fetch dimensional props. */
        const { width, height } = this.props
        /* Fetch data props. */
        const { mapData, colorData } = this.props
        /* Fetch event props */
        const {
            mouseEnter=(EMPTY_FUNC),
            mouseMove=(EMPTY_FUNC),
            mouseLeave=(EMPTY_FUNC)
        } = this.props
        /* Fetch color settings from state. */
        const { colorList, domain } = this.state
        /* Generate path function. fitSize allows for fitting map into SVG dimensions. */
        const path = d3.geoPath().projection(
            /* Generate if mapData is provided. */
            (mapData !== null) && 
            d3.geoIdentity()
            .fitSize(
                [width, height],
                {
                    type: "GeometryCollection",
                    geometries: mapData.map( ( { geo } ) => (geo) )
                }
            )
        )
        /* Color generator for given data point. Determined based on domain. */
        const getColor = (dataPoint) => {
            /* Find group index by thresholding. */
            const groupIndex = domain.map((item) => (
                (dataPoint || 0) <= item
            )).reduce((item1, item2) => (
                item1+item2
            ), 0)
            /* Return color associated with group index. */
            return colorList[
                Math.min(colorList.length-1, colorList.length-groupIndex)
            ]
        }
        /* Generate legend text for given index. */
        const getLegendText = (domain, index) => {
            if (index === 0) {
                return (
                    <span>
                        {`< ${domain[index].toLocaleString()}`}
                    </span>
                )
            }
            if (index === domain.length-1) {
                return (
                    <span>
                        {`> ${domain[index-1].toLocaleString()}`}
                    </span>
                )
            }
            return (
                <React.Fragment>
                    <span> {`${domain[index-1].toLocaleString()}`} </span>
                    <span> - </span> 
                    <span> {`${domain[index].toLocaleString()}`} </span>
                </React.Fragment>
            )
        }
        /* Render */
        return (
            (mapData !== null) && 
            <div className="d-flex flex-column" style={{maxWidth: `${Math.round((600/height)*width)}px`}}>
                <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio='xMidYMid meet'>
                    <g id='map'>
                        { mapData.map(({name, geo}) => (
                        <path
                            key={name}
                            d={path(geo)}
                            fill={getColor(colorData[name]?.confirmed, name)}
                            onMouseEnter={(event) => {mouseEnter(event, name)}}
                            onMouseMove={(event) => {mouseMove(event, name)}}
                            onMouseLeave={(event) => {mouseLeave(event, name)}}
                        />
                        )) }
                    </g>
                </svg>
                <div id="map-legend" className="row mx-0 w-100 pt-4">
                    { domain.map((item, index) => (
                    <div className="col px-0" key={index}>
                        <div
                            className="w-100"
                            style={{
                                height: "1rem",
                                backgroundColor: colorList[index]
                            }}
                        />
                        <p className="mb-0 text-center w-100 px-2">
                            { getLegendText(domain, index) }
                        </p>
                    </div>
                    )) }
                </div>
            </div>
        )
    }
}
